import { useCallback } from 'react';

import type { AuthConfig } from '@/auth/authUtils';
import { clearAuthContext, finalizeUserSession, getAuthContext } from '@/auth/authUtils';
import type { UserSession } from '@/auth/sessionTypes';

// SAML-specific browser flow helpers. The frontend server validates assertions
// and exposes a one-time session handoff; this hook consumes that handoff.
export const useSamlAuth = () => {
  const startSamlLogin = useCallback((authConfig: AuthConfig) => {
    const samlStartUrl = new URL(authConfig.login.start_url || '', window.location.origin);
    samlStartUrl.searchParams.set('return_url', `${window.location.origin}/auth/callback?session=1`);
    window.location.href = samlStartUrl.toString();
  }, []);

  const completeSamlLogin = useCallback(async (): Promise<UserSession> => {
    try {
      const authContext = getAuthContext();
      const response = await fetch('/api/v1/auth/session');
      if (!response.ok) {
        throw new Error('Failed to load authenticated session');
      }

      const sessionData = (await response.json()) as UserSession;
      const userSession: UserSession = {
        ...sessionData,
        email: sessionData.email || authContext?.email,
        authentication_id: sessionData.authentication_id || authContext?.authenticationId,
        authenticated_at: sessionData.authenticated_at || new Date().toISOString(),
        auth_protocol: 'saml',
      };

      return finalizeUserSession(userSession);
    } catch (err) {
      clearAuthContext();
      throw err;
    }
  }, []);

  return {
    completeSamlLogin,
    startSamlLogin,
  };
};
